import type { MotionPropsFor } from '@hover/blueprint';
import { forwardRef, motion, useTheme } from '@hover/blueprint';
import { isDefined } from '@hover/utilities';
import type { Transition } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';

import { productBrightnessWithDefault } from '../../helpers/color';

import type { SelectPaletteButtonProps } from './SelectPaletteButton';
import { SelectPaletteButton } from './SelectPaletteButton';
import { useCustomization } from './useCustomization';
import { usePalettes } from './usePalettes';

const EXPAND_DELAY = 500;
const COLLAPSE_DELAY = 1200;

export interface CustomizeSelectPaletteButtonProps
  extends MotionPropsFor<Partial<SelectPaletteButtonProps>> {}

const MotionSelectPaletteButton = motion(SelectPaletteButton);

export const CustomizeSelectPaletteButton = forwardRef<
  CustomizeSelectPaletteButtonProps,
  'div'
>((props, ref) => {
  const { category } = useCustomization();
  const {
    isSelectPaletteOpen,
    selectedPalette,
    palettePaintColors,
    onSelectPaletteOpen,
  } = usePalettes();

  const colors = useMemo(() => {
    if (palettePaintColors.length === 0) return undefined;

    const sorted = palettePaintColors
      .slice()
      .sort(
        (a, b) =>
          productBrightnessWithDefault(b) - productBrightnessWithDefault(a),
      );

    // After sorting the colors by brightness, sample from ¼ and ½ of the way
    // through the list to get a wider range of colors that's still a nice ombré
    const samples = [
      sorted[0],
      sorted[Math.floor(sorted.length / 4)],
      sorted[Math.floor(sorted.length / 2)],
    ]
      .filter(isDefined)
      .map(color => color.hex);

    if (samples.length === 0) return undefined;

    return samples;
  }, [palettePaintColors]);

  const {
    motion: { framer },
  } = useTheme();

  const [isExpanded, setIsExpanded] = useState(isSelectPaletteOpen);
  const [expandTransition, setExpandTransition] = useState<Transition>({
    duration: 0,
  });

  // For paint, we expand and collapse the button depending on whether the
  // select palette sheet is open, but we delay the collapse animation a bit so
  // the user briefly sees the palette label, and we delay and disable the
  // expand animation so the button expands while hidden by the sheet.
  useEffect(() => {
    const timer = setTimeout(
      () => {
        setExpandTransition(
          isSelectPaletteOpen ? { duration: 0 } : framer.soft,
        );
        setIsExpanded(isSelectPaletteOpen);
      },
      isSelectPaletteOpen ? EXPAND_DELAY : COLLAPSE_DELAY,
    );

    return () => clearTimeout(timer);
  }, [isSelectPaletteOpen, framer.soft, isExpanded]);

  return (
    <AnimatePresence initial={false} mode="popLayout">
      {category && (
        <MotionSelectPaletteButton
          colors={category.type === 'paint' ? colors : undefined}
          expandTransition={expandTransition}
          isExpanded={category.type === 'paint' ? isExpanded : true}
          label={selectedPalette}
          onClick={onSelectPaletteOpen}
          {...props}
          key={category.type}
          ref={ref}
        />
      )}
    </AnimatePresence>
  );
});
